// Brand Portal shell — auth, layout, and tab switching only.
// All page bodies live in /components/brand/tabs and /components/brand.
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import {
  BarChart3, Briefcase, Users, FileText, DollarSign, Settings,
  LogOut, Calendar as CalendarIcon, Shield, Search, BookOpen, Sparkles,
  MessageCircle, FileSignature,
} from 'lucide-react';
import { Toaster } from 'sonner';

import KnowledgeBase from '../components/KnowledgeBase';
import AIChatWidget from '../components/AIChatWidget';
import { BrandContentLibrary } from '../components/ContentLibrary';
import { BrandAlliesSection } from '../components/EngagementComponents';

import { API, BrandCtx } from '../components/brand/portalShared';
import BrandLogin from '../components/brand/BrandLogin';
import PaymentGate from '../components/brand/PaymentGate';
import Dashboard from '../components/brand/tabs/Dashboard';
import CampaignsList from '../components/brand/tabs/CampaignsList';
import Invoices from '../components/brand/tabs/Invoices';
import BrandCalendar from '../components/brand/tabs/BrandCalendar';
import PortalSettings from '../components/brand/tabs/PortalSettings';
import BrandProspecting from '../components/brand/tabs/BrandProspecting';
import DraftReview from '../components/brand/tabs/DraftReview';
import BrandInsights from '../components/brand/tabs/BrandInsights';
import BrandServices from '../components/brand/tabs/BrandServices';
import Messages from '../components/brand/tabs/Messages';
import MyProposals from '../components/brand/tabs/MyProposals';

export default function BrandPortal() {
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [tab, setTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'dashboard';
  });
  const [deliverableCounts, setDeliverableCounts] = useState({});

  // Keep the tab in sync with `?tab=…` when we navigate inside the portal
  // (e.g. deep-links from the campaign amplify-upsell CTA).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('tab');
    if (t && t !== tab) setTab(t);
  }, [location.search, tab]);

  const getBrandHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('propost_brand_token')}` });

  useEffect(() => {
    const token = localStorage.getItem('propost_brand_token');
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/portal/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const dashData = r.data;
        if (dashData.brand) {
          setBrand({ ...dashData.brand, _dashboard: dashData });
        } else {
          setBrand(dashData);
        }
      })
      .catch(() => { localStorage.removeItem('propost_brand_token'); })
      .finally(() => setLoading(false));
  }, []);

  // Poll deliverable counts every 30s
  useEffect(() => {
    if (!brand) return;
    const fetchCounts = () => {
      axios.get(`${API}/portal/deliverables-counts`, { headers: getBrandHeaders() })
        .then(r => setDeliverableCounts(r.data || {}))
        .catch(() => {});
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 30000);
    return () => clearInterval(t);
  }, [brand]);

  const handleLogout = () => { localStorage.removeItem('propost_brand_token'); setBrand(null); };

  if (loading) return <div className="min-h-screen bg-[#090E33] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#FF4343] border-t-transparent rounded-full animate-spin" /></div>;
  if (!brand) return <BrandLogin onLogin={(b) => setBrand(b)} />;

  const tabs = [
    { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { key: 'messages', label: 'Messages', icon: MessageCircle, badge: deliverableCounts.unread_messages || 0 },
    { key: 'proposals', label: 'Proposals', icon: FileSignature, badge: deliverableCounts.pending_proposals || 0 },
    { key: 'campaigns', label: 'Campaigns', icon: Briefcase },
    { key: 'services', label: 'Amplify + Produce', icon: Sparkles },
    { key: 'prospecting', label: 'Find Executives', icon: Search },
    { key: 'drafts', label: 'Draft Review', icon: FileText },
    { key: 'insights', label: 'Deliverables Hub', icon: Briefcase, badge: deliverableCounts.total || 0 },
    { key: 'content-library', label: 'Content Library', icon: Shield },
    { key: 'allies', label: 'Allies', icon: Users },
    { key: 'calendar', label: 'Calendar', icon: CalendarIcon },
    { key: 'invoices', label: 'Invoices', icon: DollarSign },
    { key: 'help', label: 'Help Center', icon: BookOpen },
    { key: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <BrandCtx.Provider value={brand}>
      <Toaster position="top-right" theme="dark" richColors />
      <div className="min-h-screen bg-[#090E33]" data-testid="brand-portal">
        {/* Top bar */}
        <div className="border-b border-white/10 bg-[#111827]/50">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/pflz8b2v_freddy-logo-symbol.png" alt="" className="h-7 w-7" />
              <span className="font-display text-lg font-bold"><span className="text-[#FF4343]">Freddy</span>Leads</span>
              <span className="text-[10px] text-[#6B7280] ml-2">Brand Portal</span>
              {brand?.name && <span className="text-xs text-white/60 ml-1 hidden sm:inline">/ {brand.name}</span>}
            </div>
            <button onClick={handleLogout} className="text-[#6B7280] hover:text-white text-xs flex items-center gap-1"><LogOut className="w-3.5 h-3.5" /> Sign Out</button>
          </div>
        </div>
        {/* Nav + Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          {/* Mobile: horizontal tabs */}
          <div className="flex sm:hidden overflow-x-auto gap-0.5 border-b border-white/10 pb-2 mb-4 -mx-4 px-4 scrollbar-hide">
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`portal-tab-${t.key}`}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs whitespace-nowrap flex-shrink-0 border-b-2 relative ${tab === t.key ? 'text-[#FF4343] border-[#FF4343]' : 'text-[#9CA3AF] border-transparent'}`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
                {t.badge > 0 && <span className="bg-[#FF4343] text-[#090E33] text-[9px] font-bold rounded-full px-1.5 py-0.5 leading-none ml-1" data-testid={`portal-tab-badge-${t.key}`}>{t.badge}</span>}
              </button>
            ))}
          </div>
          <div className="flex gap-8">
            {/* Desktop: sidebar */}
            <div className="w-48 flex-shrink-0 space-y-1 hidden sm:block">
              {tabs.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`portal-tab-lg-${t.key}`}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${tab === t.key ? 'text-[#FF4343] bg-[#FF4343]/5 border-l-2 border-[#FF4343]' : 'text-[#9CA3AF] hover:text-white'}`}>
                  <t.icon className="w-4 h-4" />
                  <span className="flex-1 text-left">{t.label}</span>
                  {t.badge > 0 && <span className="bg-[#FF4343] text-[#090E33] text-[9px] font-bold rounded-full px-1.5 py-0.5 leading-none" data-testid={`portal-tab-lg-badge-${t.key}`}>{t.badge}</span>}
                </button>
              ))}
            </div>
            <div className="flex-1 min-w-0">
              {tab === 'dashboard' && <Dashboard onNavigate={setTab} />}
              {tab === 'messages' && <Messages />}
              {tab === 'proposals' && <MyProposals />}
              {tab === 'campaigns' && <CampaignsList />}
              {tab === 'services' && <BrandServices />}
              {tab === 'prospecting' && <PaymentGate brand={brand}><BrandProspecting /></PaymentGate>}
              {tab === 'drafts' && <DraftReview />}
              {tab === 'insights' && <BrandInsights />}
              {tab === 'content-library' && <BrandContentLibrary getHeaders={getBrandHeaders} />}
              {tab === 'allies' && <BrandAlliesSection getHeaders={getBrandHeaders} />}
              {tab === 'calendar' && <BrandCalendar />}
              {tab === 'invoices' && <Invoices />}
              {tab === 'help' && <KnowledgeBase audience="brand" />}
              {tab === 'settings' && <PortalSettings />}
            </div>
          </div>
        </div>
      </div>
      <AIChatWidget audience="brand" getHeaders={getBrandHeaders} contactEmail={brand?.portal_email} />
    </BrandCtx.Provider>
  );
}
